import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap5';
import * as Yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';

import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import HorizontalSelectField from 'components/new_ui/Form/HorizontalSelectField';
import SubmitButton from 'components/Widgets/SubmitButton';
import useCareerLevels from 'components/Common/CareerLevels/Hooks/useCareerLevels';
import useTimeZones from 'components/Common/TimeZones/Hooks/useTimeZones';
import useLanguages from 'components/Common/Languages/Hooks/useLanguages';
import Actions from 'components/Members/Actions';
import { useContext as useUsersContext } from 'components/Users/Context';

export default function Index({ submitText, afterSubmit, previousLink }) {
  const careerLevels = useCareerLevels();
  const { timeZones } = useTimeZones();
  const { languages } = useLanguages();

  const { state: stateUsers } = useUsersContext();
  const member = stateUsers.user?.attributes?.specific;

  if (!member) {
    return (
      <section>
        <LoadingPlaceholder text="Loading..." className="py-5" />
      </section>
    );
  }

  const formSchema = Yup.object().shape({
    careerLevel: Yup.string().required('Job Role is required').nullable(),
  });

  const onSubmitHundler = (values, { setSubmitting, setErrors }) => {
    const params = {
      career_level_id: values.careerLevel,
      time_zone: values.timeZone,
      prefered_language_id: values.preferredLanguage,
      lgbt_preference: values.lgbtPreference,
      mom_preference: values.momPreference,
    };

    Actions.update(
      member.id,
      params,
      (_updatedMember) => {
        setSubmitting(false);
        afterSubmit();
      },
      (errors) => {
        const newErrors = {};

        errors.forEach((item) => {
          newErrors[item.source.pointer.replace('/data/attributes/', '')] = item.detail;
        });
        setErrors(newErrors);
        setSubmitting(false);
      },
    );
  };

  return (
    <Formik
      validationSchema={formSchema}
      initialValues={{
        careerLevel: member.career_level_id?.toString(),
        timeZone: member.time_zone,
        preferredLanguage: member.prefered_language_id?.toString(),
        lgbtPreference: member.lgbt_preference,
        momPreference: member.mom_preference,
      }}
      onSubmit={onSubmitHundler}
    >
      {({ isSubmitting }) => (
        <FormikForm className="needs-validation" noValidate="">
          <section>
            <Row>
              <Col>
                <HorizontalSelectField
                  name="careerLevel"
                  label="Which of these best describes your job role?"
                  placeholder={careerLevels === null ? 'Loading...' : 'Select'}
                  options={careerLevels === null ? [] : careerLevels.map((el) => ({
                    label: el.attributes.title,
                    value: el.id,
                  }))}
                  isClearable={false}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <HorizontalSelectField
                  name="timeZone"
                  label="What is your time zone?"
                  placeholder={timeZones === null ? 'Loading...' : 'Select'}
                  options={timeZones === null ? [] : timeZones}
                  isClearable={false}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <HorizontalSelectField
                  name="preferredLanguage"
                  label="What language would you like to use in your coaching session?"
                  placeholder={languages === null ? 'Loading...' : 'Select'}
                  options={languages === null ? [] : languages.map((el) => ({
                    label: el.attributes.title,
                    value: el.id,
                  }))}
                  isClearable={false}
                />
              </Col>
            </Row>

            <Row className="d-none">
              <Col>
                <HorizontalSelectField
                  name="lgbtPreference"
                  label="I would prefer a coach who is a member of the LGBTQ+ community (if available)."
                  placeholder="Select"
                  options={[
                    { label: 'No preference', value: false },
                    { label: 'Yes', value: true },
                  ]}
                  isClearable={false}
                />
              </Col>
            </Row>

            <Row className="d-none">
              <Col>
                <HorizontalSelectField
                  name="momPreference"
                  label="I would prefer a coach who is, or has been, a working mom."
                  placeholder="Select"
                  options={[
                    { label: 'No preference', value: false },
                    { label: 'Yes', value: true },
                  ]}
                  isClearable={false}
                />
              </Col>
            </Row>
          </section>

          <footer className={previousLink ? 'd-flex justify-content-between' : ''}>
            {previousLink && (
              <Link
                to={previousLink}
                className={`btn btn-outline-primary ${isSubmitting && 'disabled'}`}
              >
                Previous
              </Link>
            )}

            <SubmitButton loading={isSubmitting}>
              {submitText}
            </SubmitButton>
          </footer>
        </FormikForm>
      )}
    </Formik>
  );
}

Index.propTypes = {
  submitText: PropTypes.string,
  afterSubmit: PropTypes.func.isRequired,
  previousLink: PropTypes.string,
};

Index.defaultProps = {
  submitText: 'Save',
  previousLink: undefined,
};
